// ── Clock-In donut: what goes in each slice ──────────────────────────────────
// Shared by the donut on the Clock-In stats page (FocusPieChart) and the donut
// home-screen widget (buildFocusDonutPayload in capacitor/widgetSync.js), so the
// two can never disagree about which week it is, what a slice holds, or its
// colour. The widget's Java (FocusDonutWidget) re-sums the per-day totals for
// the phone's current week with the same ordering — keep the two in step.
import { hexToRgb, rgbToHsl, hslToRgb, rgbToHex } from "../theme/colorEngine";

// Every logged session with no linked category lands in this one slice.
export const NO_CATEGORY = "Uncategorized";
export const NO_CATEGORY_COLOR = "#c4c0cc";
const DEFAULT_CATEGORY_COLOR = "#d4789a";

// Local "yyyy-mm-dd" — never toISOString() (UTC, a day early before ~8am in Manila).
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// The week `offset` weeks from the one containing `now`, starting on the day
// Settings → "Week starts on" says (Sunday or Monday). Dates at local midnight.
export function focusWeekRange(weekStart = "sunday", offset = 0, now = new Date()) {
  const dow = now.getDay();
  const back = weekStart === "monday" ? (dow + 6) % 7 : dow;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - back + offset * 7);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return { start, end, from: ymd(start), to: ymd(end) };
}

// A session's category, from its stored id (preferred) or its label.
export function categoryForSession(s, categories = []) {
  if (s.categoryId) { const c = categories.find((c) => c.id === s.categoryId); if (c) return c; }
  const t = s.categoryLabel?.trim();
  if (t) { const c = categories.find((c) => c.title === t); if (c) return c; }
  return null;
}

export const sliceNameForSession = (s, categories = []) => categoryForSession(s, categories)?.title || NO_CATEGORY;

// Each slice keeps the category's own hue but is set to one medium intensity
// (Jade, 2026-10-04: the pastel picks read washed out, and others too strong —
// she asked for the Study slice's look on every slice). Same saturation and
// lightness for every slice, so the donut reads as one even set whatever
// pastel or vivid colour each category was given. Near-greys keep their own
// saturation so a deliberately grey category doesn't turn colourful.
export const SLICE_SATURATION = 0.78;
export const SLICE_LIGHTNESS = 0.7;

export function boostSaturation(hex, amount = 0.12) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const rgb = hslToRgb(h, Math.min(1, s + amount), l);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

export function mediumIntensity(hex) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s } = rgbToHsl(r, g, b);
  const rgb = hslToRgb(h, s < 0.1 ? s : SLICE_SATURATION, SLICE_LIGHTNESS);
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

// Per-category intensity (Jade, 2026-10-04): the slider in the Clock-In stats
// page stores `sliceOpacity` on the category itself (so it syncs with the rest of
// the category and reaches the widget through the same `categories`). 1 = the
// medium intensity above, lower = fainter. Blended with white rather than made
// transparent, because the widget has no stable background to be transparent
// over and the app's cards are white anyway.
export const SLICE_OPACITY_MIN = 0.25;
export const clampSliceOpacity = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(1, Math.max(SLICE_OPACITY_MIN, n)) : 1;
};

export function withSliceOpacity(hex, opacity) {
  const o = clampSliceOpacity(opacity);
  if (o >= 1) return hex;
  const { r, g, b } = hexToRgb(hex);
  const mix = (c) => Math.round(255 - (255 - c) * o);
  return rgbToHex(mix(r), mix(g), mix(b));
}

// Custom slice colour (Jade, 2026-10-06): the stats page's "Edit slice colours"
// lets a user pick the donut colour of a category outright. Stored as
// `sliceHex` on the category (syncs, reaches the widget through the same
// `categories`), used ONLY by the donut, its legend and the widget — the
// category's own `color` everywhere else is untouched. A picked colour is used
// as-is: no medium-intensity normalising and no hue nudging, since the user
// chose it. Intensity (sliceOpacity) still applies on top.
const SLICE_HEX_RE = /^#[0-9a-f]{6}$/i;
export const customSliceHex = (cat) =>
  (cat && typeof cat.sliceHex === "string" && SLICE_HEX_RE.test(cat.sliceHex)) ? cat.sliceHex.toLowerCase() : null;

// Distinct hues (Jade, 2026-10-04: two blues and an orange/yellow pair looked
// identical on the donut). Category colours are free picks, so two of them can
// land a few degrees apart. Walking the categories in list order, a hue closer
// than SLICE_MIN_HUE_GAP to one already placed is nudged to the nearest free
// spot (up to 90 degrees either way); earlier categories keep their own hue, so
// adding a category never recolours the ones above it. 24 degrees is enough to
// tell sky blue from periwinkle, yet small enough that a crowded region doesn't
// shove a category into another colour family. It depends on the whole category
// list, not on which slices have time this week, so a slice keeps its colour
// from week to week — and the widget gets the same colours, because its payload
// goes through sliceColor with the same list. Near-greys are left alone. Only
// the donut, legend and widget slices are nudged; the category's own colour in
// Spaces and the Planner is unchanged.
export const SLICE_MIN_HUE_GAP = 24;
const hueGap = (a, b) => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; };
const hueCache = new WeakMap();

export function distinctHues(categories = []) {
  const cached = hueCache.get(categories);
  if (cached) return cached;
  const out = new Map();
  const placed = [];
  const gapTo = (h) => placed.reduce((m, p) => Math.min(m, hueGap(h, p)), 180);
  (categories || []).forEach((c) => {
    const title = c?.title;
    if (!title || out.has(title)) return;
    if (customSliceHex(c)) return; // a picked colour is used as-is, never nudged
    const { r, g, b } = hexToRgb(c.color || DEFAULT_CATEGORY_COLOR);
    const { h, s } = rgbToHsl(r, g, b);
    if (s < 0.1) { out.set(title, null); return; }
    let best = h, bestGap = gapTo(h);
    for (let off = 6; off <= 90 && bestGap < SLICE_MIN_HUE_GAP; off += 6) {
      for (const sign of [1, -1]) {
        const cand = (h + sign * off + 360) % 360;
        const gap = gapTo(cand);
        if (gap > bestGap) { best = cand; bestGap = gap; }
      }
    }
    placed.push(best);
    out.set(title, best);
  });
  hueCache.set(categories, out);
  return out;
}

export function sliceColor(name, categories = []) {
  if (name === NO_CATEGORY) return NO_CATEGORY_COLOR;
  const cat = categories.find((c) => c.title === name);
  const custom = customSliceHex(cat);
  const hue = distinctHues(categories).get(name);
  const base = custom ? custom : hue == null
    ? mediumIntensity(cat?.color || DEFAULT_CATEGORY_COLOR)
    : (() => { const rgb = hslToRgb(hue, SLICE_SATURATION, SLICE_LIGHTNESS); return rgbToHex(rgb.r, rgb.g, rgb.b); })();
  return withSliceOpacity(base, cat?.sliceOpacity);
}

// Biggest first; equal minutes by name, so the order never depends on which
// session happened to be logged first (the widget sorts the same way).
export const compareSlices = (a, b) => b.mins - a.mins || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);

// One slice per category that has time logged in `sessions` — every one, no
// cap and no "Other" (Jade, 2026-10-03: the old 6-slice limit is gone).
export function donutSlices(sessions = [], categories = []) {
  const mins = {};
  const counts = {};
  sessions.forEach((s) => {
    const name = sliceNameForSession(s, categories);
    mins[name] = (mins[name] || 0) + (s.minutes || 0);
    counts[name] = (counts[name] || 0) + 1;
  });
  return Object.keys(mins)
    .map((name) => ({ name, mins: mins[name], count: counts[name], color: sliceColor(name, categories) }))
    .sort(compareSlices);
}

// What a row's percentage reads — the same rounding in the app's legend and on
// the widget.
export const slicePercent = (mins, totalMins) => (totalMins > 0 ? Math.round((mins / totalMins) * 100) : 0);
